import React, { useState, useMemo } from 'react';

import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const MusicalSpecialtiesTab = ({ profile, onSave }) => {
  const [formData, setFormData] = useState({
    artistProfession: profile?.artistProfession || '',
    genres: profile?.genres || [],
    equipment: profile?.equipment || [],
    venueTypes: profile?.venueTypes || [],
    eventTypes: profile?.eventTypes || [],
    portfolioFiles: profile?.portfolioFiles || [],
    certifications: profile?.certifications || [],
    socialLinks: profile?.socialLinks || [],
    yearsExperience: profile?.yearsExperience || 0,
    professionalBio: profile?.professionalBio || '',
    portfolioDescription: profile?.portfolioDescription || '',
    hourlyRate: profile?.hourlyRate || '',
    specializations: profile?.specializations || [],
    performanceStyles: profile?.performanceStyles || [],
    skillTags: profile?.skillTags || []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Artist profession options
  const professionOptions = [
    { value: 'musician', label: 'Musicien' },
    { value: 'singer', label: 'Chanteur/Chanteuse' },
    { value: 'dj', label: 'DJ' },
    { value: 'producer', label: 'Producteur' },
    { value: 'model', label: 'Mannequin' },
    { value: 'actor', label: 'Acteur/Actrice' },
    { value: 'magician', label: 'Magicien' },
    { value: 'dancer', label: 'Danseur/Danseuse' },
    { value: 'performer', label: 'Artiste de performance' },
    { value: 'other', label: 'Autre' }
  ];

  // Intelligent configuration based on profession
  const professionConfig = useMemo(() => {
    const config = {
      musician: {
        title: 'Configuration Musicien/Chanteur',
        description: 'Profil optimisé pour les musiciens et chanteurs avec intégrations streaming et uploads multimédia',
        socialPlatforms: ['soundcloud', 'spotify', 'youtube', 'instagram'],
        portfolioTypes: ['audio', 'video', 'image'],
        acceptedFiles: 'audio/*,video/*,image/*,.pdf',
        categories: ['demo', 'live_performance', 'studio_recording', 'music_video'],
        equipmentSuggestions: ['microphone', 'guitar', 'piano', 'studio_monitors', 'audio_interface', 'headphones'],
        genreSuggestions: ['pop', 'rock', 'jazz', 'classical', 'electronic', 'hip_hop', 'country', 'blues']
      },
      singer: {
        title: 'Configuration Chanteur/Chanteuse',
        description: 'Profil optimisé pour les chanteurs avec intégrations streaming et uploads multimédia',
        socialPlatforms: ['soundcloud', 'spotify', 'youtube', 'instagram', 'tiktok'],
        portfolioTypes: ['audio', 'video', 'image'],
        acceptedFiles: 'audio/*,video/*,image/*,.pdf',
        categories: ['vocal_demo', 'live_performance', 'music_video', 'cover_song'],
        equipmentSuggestions: ['microphone', 'vocal_processor', 'headphones', 'pop_filter', 'recording_booth'],
        genreSuggestions: ['pop', 'jazz', 'soul', 'r&b', 'classical', 'musical_theater', 'electronic']
      },
      dj: {
        title: 'Configuration DJ/Producteur',
        description: 'Profil optimisé pour les DJs et producteurs avec intégrations streaming et uploads multimédia',
        socialPlatforms: ['soundcloud', 'spotify', 'youtube', 'instagram'],
        portfolioTypes: ['audio', 'video', 'image'],
        acceptedFiles: 'audio/*,video/*,image/*,.pdf',
        categories: ['dj_mix', 'live_set', 'production', 'remix'],
        equipmentSuggestions: ['cdj_3000', 'pioneer_mixer', 'technics_1200', 'serato', 'rekordbox', 'studio_monitors'],
        genreSuggestions: ['house', 'techno', 'trance', 'dubstep', 'drum_bass', 'progressive', 'deep_house', 'minimal']
      },
      producer: {
        title: 'Configuration Producteur',
        description: 'Profil optimisé pour les producteurs avec intégrations streaming et uploads multimédia',
        socialPlatforms: ['soundcloud', 'spotify', 'youtube', 'instagram'],
        portfolioTypes: ['audio', 'video', 'image'],
        acceptedFiles: 'audio/*,video/*,image/*,.pdf',
        categories: ['production', 'remix', 'collaboration', 'instrumental'],
        equipmentSuggestions: ['daw_software', 'midi_keyboard', 'studio_monitors', 'audio_interface', 'synthesizer'],
        genreSuggestions: ['electronic', 'hip_hop', 'pop', 'house', 'techno', 'ambient', 'experimental']
      },
      model: {
        title: 'Configuration Mannequin/Acteur',
        description: 'Profil optimisé pour les mannequins et acteurs avec book photo et extraits vidéos',
        socialPlatforms: ['instagram', 'tiktok', 'facebook', 'linkedin'],
        portfolioTypes: ['image', 'video'],
        acceptedFiles: 'image/*,video/*,.pdf',
        categories: ['portrait', 'fashion', 'commercial', 'artistic', 'headshots'],
        equipmentSuggestions: ['professional_camera', 'lighting_kit', 'backdrop', 'props'],
        genreSuggestions: [] // Not applicable
      },
      actor: {
        title: 'Configuration Acteur/Actrice',
        description: 'Profil optimisé pour les acteurs avec book photo et extraits vidéos',
        socialPlatforms: ['instagram', 'youtube', 'linkedin', 'tiktok'],
        portfolioTypes: ['image', 'video', 'document'],
        acceptedFiles: 'image/*,video/*,.pdf',
        categories: ['headshots', 'showreel', 'theater_performance', 'film_excerpt', 'commercial'],
        equipmentSuggestions: ['professional_camera', 'lighting_kit', 'teleprompter', 'green_screen'],
        genreSuggestions: [] // Not applicable
      },
      magician: {
        title: 'Configuration Magicien',
        description: 'Profil optimisé pour les magiciens avec book photo et vidéos de performances',
        socialPlatforms: ['youtube', 'instagram', 'tiktok', 'facebook'],
        portfolioTypes: ['image', 'video'],
        acceptedFiles: 'image/*,video/*,.pdf',
        categories: ['performance_photos', 'magic_demo', 'stage_show', 'close_up_magic', 'illusion'],
        equipmentSuggestions: ['magic_props', 'stage_equipment', 'lighting', 'sound_system', 'costume'],
        genreSuggestions: [] // Not applicable
      },
      dancer: {
        title: 'Configuration Danse et Performances',
        description: 'Profil optimisé pour les danseurs et artistes de performance avec portfolio multimédia',
        socialPlatforms: ['instagram', 'tiktok', 'youtube', 'facebook'],
        portfolioTypes: ['image', 'video'],
        acceptedFiles: 'image/*,video/*,.pdf',
        categories: ['dance_performance', 'choreography', 'competition', 'music_video', 'stage_show'],
        equipmentSuggestions: ['sound_system', 'stage_lighting', 'costume', 'props', 'video_equipment'],
        genreSuggestions: [] // Not applicable
      },
      performer: {
        title: 'Configuration Artiste de Performance',
        description: 'Profil optimisé pour les artistes de performance avec portfolio multimédia adapté',
        socialPlatforms: ['instagram', 'youtube', 'tiktok', 'facebook'],
        portfolioTypes: ['image', 'video', 'document'],
        acceptedFiles: 'image/*,video/*,.pdf',
        categories: ['live_performance', 'art_installation', 'street_performance', 'theater', 'experimental'],
        equipmentSuggestions: ['performance_props', 'stage_equipment', 'lighting', 'sound_system', 'costume'],
        genreSuggestions: [] // Not applicable
      }
    };

    return config?.[formData?.artistProfession] || config?.musician;
  }, [formData?.artistProfession]);

  // Dynamic options based on profession
  const genreOptions = useMemo(() => {
    const allGenres = [
      { value: 'house', label: 'House' },
      { value: 'techno', label: 'Techno' },
      { value: 'trance', label: 'Trance' },
      { value: 'pop', label: 'Pop' },
      { value: 'rock', label: 'Rock' },
      { value: 'jazz', label: 'Jazz' },
      { value: 'classical', label: 'Classique' },
      { value: 'electronic', label: 'Électronique' },
      { value: 'hip_hop', label: 'Hip-Hop' },
      { value: 'r&b', label: 'R&B' },
      { value: 'soul', label: 'Soul' },
      { value: 'blues', label: 'Blues' },
      { value: 'country', label: 'Country' },
      { value: 'reggae', label: 'Reggae' },
      { value: 'folk', label: 'Folk' },
      { value: 'latin', label: 'Latin' },
      { value: 'world', label: 'Musique du monde' },
      { value: 'experimental', label: 'Expérimental' }
    ];

    if (professionConfig?.genreSuggestions?.length > 0) {
      return allGenres?.filter(genre => professionConfig?.genreSuggestions?.includes(genre?.value));
    }
    return allGenres;
  }, [professionConfig]);

  const equipmentOptions = useMemo(() => {
    const allEquipment = [
      { value: 'cdj_3000', label: 'CDJ-3000' },
      { value: 'pioneer_mixer', label: 'Mixeur Pioneer' },
      { value: 'technics_1200', label: 'Technics 1200' },
      { value: 'serato', label: 'Serato DJ' },
      { value: 'rekordbox', label: 'Rekordbox' },
      { value: 'microphone', label: 'Microphone' },
      { value: 'guitar', label: 'Guitare' },
      { value: 'piano', label: 'Piano' },
      { value: 'studio_monitors', label: 'Moniteurs de studio' },
      { value: 'audio_interface', label: 'Interface audio' },
      { value: 'headphones', label: 'Casque' },
      { value: 'synthesizer', label: 'Synthétiseur' },
      { value: 'midi_keyboard', label: 'Clavier MIDI' },
      { value: 'professional_camera', label: 'Appareil photo professionnel' },
      { value: 'lighting_kit', label: 'Kit d\'éclairage' },
      { value: 'sound_system', label: 'Système sonore' },
      { value: 'stage_equipment', label: 'Équipement de scène' }
    ];

    if (professionConfig?.equipmentSuggestions?.length > 0) {
      return allEquipment?.filter(eq => professionConfig?.equipmentSuggestions?.includes(eq?.value));
    }
    return allEquipment;
  }, [professionConfig]);

  const venueTypeOptions = [
    { value: 'nightclub', label: 'Boîte de nuit' },
    { value: 'festival', label: 'Festival' },
    { value: 'bar', label: 'Bar/Pub' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'wedding', label: 'Mariage' },
    { value: 'corporate', label: 'Événement d\'entreprise' },
    { value: 'private_party', label: 'Fête privée' },
    { value: 'theater', label: 'Théâtre' },
    { value: 'concert_hall', label: 'Salle de concert' },
    { value: 'studio', label: 'Studio' },
    { value: 'outdoor', label: 'Extérieur' },
    { value: 'fashion_show', label: 'Défilé de mode' }
  ];

  const eventTypeOptions = [
    { value: 'regular_gig', label: 'Prestation régulière' },
    { value: 'special_event', label: 'Événement spécial' },
    { value: 'residency', label: 'Résidence' },
    { value: 'guest_set', label: 'Set invité' },
    { value: 'headliner', label: 'Tête d\'affiche' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'recording_session', label: 'Session d\'enregistrement' },
    { value: 'photo_shoot', label: 'Séance photo' },
    { value: 'video_shoot', label: 'Tournage vidéo' },
    { value: 'performance_art', label: 'Art performance' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialLinkChange = (platform, url) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev?.socialLinks,
        [platform]: url
      }
    }));
  };

  const handleFileUpload = async (event, type) => {
    const files = Array.from(event?.target?.files);
    setUploadingFile(true);

    try {
      // Mock file upload - in real app, upload to server
      const uploadedFiles = files?.map(file => ({
        id: Date.now() + Math.random(),
        name: file?.name,
        type: file?.type,
        size: file?.size,
        url: URL.createObjectURL(file),
        uploadDate: new Date()?.toISOString(),
        category: type === 'portfolioFiles' ? 'portfolio' : 'certification'
      }));

      setFormData(prev => ({
        ...prev,
        [type]: [...prev?.[type], ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = (fileId, type) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev?.[type]?.filter(file => file?.id !== fileId)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      artistProfession: profile?.artistProfession || '',
      genres: profile?.genres || [],
      equipment: profile?.equipment || [],
      venueTypes: profile?.venueTypes || [],
      eventTypes: profile?.eventTypes || [],
      portfolioFiles: profile?.portfolioFiles || [],
      certifications: profile?.certifications || [],
      socialLinks: profile?.socialLinks || [],
      yearsExperience: profile?.yearsExperience || 0,
      professionalBio: profile?.professionalBio || '',
      portfolioDescription: profile?.portfolioDescription || '',
      hourlyRate: profile?.hourlyRate || '',
      specializations: profile?.specializations || [],
      performanceStyles: profile?.performanceStyles || [],
      skillTags: profile?.skillTags || []
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Profil Artiste Intelligent</h2>
          <p className="text-sm text-muted-foreground">
            {professionConfig?.description}
          </p>
        </div>
        {!isEditing ? (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
          >
            Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Sauvegarder
            </Button>
          </div>
        )}
      </div>

      {/* Profession Selection */}
      <div className="bg-accent/5 rounded-lg p-4 border border-accent/20">
        <Select
          label="Type d'artiste"
          description="Sélectionnez votre profession principale pour adapter votre profil"
          options={professionOptions}
          value={formData?.artistProfession}
          onChange={(value) => handleInputChange('artistProfession', value)}
          disabled={!isEditing}
          required
        />
        {formData?.artistProfession && (
          <div className="mt-3 p-3 bg-background rounded border">
            <h4 className="font-medium text-foreground text-sm">{professionConfig?.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">{professionConfig?.description}</p>
          </div>
        )}
      </div>

      {/* Professional Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Années d'expérience"
          type="number"
          value={formData?.yearsExperience}
          onChange={(value) => handleInputChange('yearsExperience', parseInt(value) || 0)}
          disabled={!isEditing}
          min="0"
          max="50"
        />
        <Input
          label="Tarif horaire (€)"
          type="number"
          value={formData?.hourlyRate}
          onChange={(value) => handleInputChange('hourlyRate', value)}
          disabled={!isEditing}
          min="0"
          step="0.01"
        />
      </div>

      {/* Bio professionnel */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Biographie professionnelle
        </label>
        <textarea
          className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-accent focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
          rows="4"
          value={formData?.professionalBio}
          onChange={(e) => handleInputChange('professionalBio', e?.target?.value)}
          disabled={!isEditing}
          placeholder="Décrivez votre expérience professionnelle, vos spécialités et ce qui vous distingue..."
        />
      </div>

      {/* Social Media Integration */}
      {formData?.artistProfession && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Icon name="Link" size={20} />
            Intégrations Réseaux Sociaux
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {professionConfig?.socialPlatforms?.map((platform) => (
              <div key={platform} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Icon 
                  name={platform === 'soundcloud' ? 'Music' : 
                        platform === 'spotify' ? 'Music' :
                        platform === 'youtube' ? 'Video' :
                        platform === 'instagram' ? 'Instagram' :
                        platform === 'tiktok'? 'Video' : 'Link'} 
                  size={20} 
                  className="text-accent" 
                />
                <div className="flex-1">
                  <Input
                    label={platform?.charAt(0)?.toUpperCase() + platform?.slice(1)}
                    value={formData?.socialLinks?.[platform] || ''}
                    onChange={(value) => handleSocialLinkChange(platform, value)}
                    disabled={!isEditing}
                    placeholder={`https://${platform}.com/votre-profil`}
                    className="text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic fields based on profession */}
      {formData?.artistProfession && genreOptions?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Genres/Styles"
            description="Vos spécialités artistiques"
            options={genreOptions}
            value={formData?.genres}
            onChange={(value) => handleInputChange('genres', value)}
            disabled={!isEditing}
            multiple
            searchable
          />

          {equipmentOptions?.length > 0 && (
            <Select
              label="Équipement/Matériel"
              description="Matériel que vous maîtrisez"
              options={equipmentOptions}
              value={formData?.equipment}
              onChange={(value) => handleInputChange('equipment', value)}
              disabled={!isEditing}
              multiple
              searchable
            />
          )}
        </div>
      )}

      {/* Venue and Event Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Types de lieux"
          description="Lieux où vous préférez travailler"
          options={venueTypeOptions}
          value={formData?.venueTypes}
          onChange={(value) => handleInputChange('venueTypes', value)}
          disabled={!isEditing}
          multiple
          searchable
        />

        <Select
          label="Types d'événements"
          description="Formats d'événements préférés"
          options={eventTypeOptions}
          value={formData?.eventTypes}
          onChange={(value) => handleInputChange('eventTypes', value)}
          disabled={!isEditing}
          multiple
          searchable
        />
      </div>

      {/* Intelligent Portfolio Section */}
      {formData?.artistProfession && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
              <Icon name="Folder" size={20} />
              {['model', 'actor', 'magician', 'dancer', 'performer']?.includes(formData?.artistProfession) ? 'Book/Portfolio' : 'Portfolio Multimédia'}
            </h3>
            {isEditing && (
              <div className="flex gap-2">
                <input
                  type="file"
                  id="portfolio-upload"
                  multiple
                  accept={professionConfig?.acceptedFiles}
                  onChange={(e) => handleFileUpload(e, 'portfolioFiles')}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('portfolio-upload')?.click()}
                  loading={uploadingFile}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {['model', 'actor']?.includes(formData?.artistProfession) ? 'Ajouter photos/vidéos' : 
                   formData?.artistProfession === 'magician' ? 'Ajouter photos/vidéos' :
                   ['musician', 'singer', 'dj', 'producer']?.includes(formData?.artistProfession) ? 'Ajouter MP3/vidéos/images' :
                   'Ajouter fichiers'}
                </Button>
              </div>
            )}
          </div>

          {/* Portfolio description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description du portfolio
            </label>
            <textarea
              className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-accent focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
              rows="2"
              value={formData?.portfolioDescription}
              onChange={(e) => handleInputChange('portfolioDescription', e?.target?.value)}
              disabled={!isEditing}
              placeholder="Décrivez votre portfolio et ce qu'il représente..."
            />
          </div>

          {formData?.portfolioFiles?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {formData?.portfolioFiles?.map((file) => (
                <div key={file?.id} className="bg-muted rounded-lg p-4 relative">
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => handleRemoveFile(file?.id, 'portfolioFiles')}
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon 
                        name={file?.type?.startsWith('audio/') ? 'Music' : 
                              file?.type?.startsWith('video/') ? 'Video' :
                              file?.type?.startsWith('image/') ? 'Image' : 'File'} 
                        size={20} 
                        className="text-accent" 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file?.size / 1024 / 1024)?.toFixed(1)} MB
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="FolderOpen" size={48} className="mx-auto mb-4 opacity-50" />
              <p>
                {['model', 'actor']?.includes(formData?.artistProfession) ? 'Aucune photo ou vidéo ajoutée' :
                 formData?.artistProfession === 'magician' ? 'Aucune photo ou vidéo de performance ajoutée' :
                 ['musician', 'singer', 'dj', 'producer']?.includes(formData?.artistProfession) ? 'Aucun fichier audio/vidéo ajouté' :
                 'Aucun fichier portfolio ajouté'}
              </p>
              {isEditing && (
                <p className="text-sm mt-2">
                  {['model', 'actor']?.includes(formData?.artistProfession) ? 'Ajoutez vos meilleures photos et extraits vidéos' :
                   formData?.artistProfession === 'magician' ? 'Ajoutez des photos et vidéos de vos performances magiques' :
                   ['musician', 'singer', 'dj', 'producer']?.includes(formData?.artistProfession) ? 'Ajoutez vos mixes, morceaux et vidéos' :
                   'Ajoutez vos créations et performances'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Certifications Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
            <Icon name="Award" size={20} />
            Certifications & Formations
          </h3>
          {isEditing && (
            <div className="flex gap-2">
              <input
                type="file"
                id="certification-upload"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, 'certifications')}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('certification-upload')?.click()}
                loading={uploadingFile}
                iconName="Upload"
                iconPosition="left"
              >
                Ajouter certificat
              </Button>
            </div>
          )}
        </div>

        {formData?.certifications?.length > 0 ? (
          <div className="space-y-3">
            {formData?.certifications?.map((cert) => (
              <div key={cert?.id} className="flex items-center justify-between bg-muted rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={20} className="text-success" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{cert?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Ajouté le {new Date(cert.uploadDate)?.toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveFile(cert?.id, 'certifications')}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Award" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Aucune certification ajoutée</p>
            {isEditing && (
              <p className="text-sm mt-2">Ajoutez vos diplômes et certifications professionnelles</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicalSpecialtiesTab;